"use client";

import Box from "@mui/material/Box";
import { VideoEmbed } from "../VideoEmbed";
import { parseVideoEmbed } from "../videoEmbedParser";
import { BrandHeader } from "./successPage/BrandHeader";
import { DefaultScreen } from "./successPage/DefaultScreen";
import { FinancialAidScreen } from "./successPage/FinancialAidScreen";
import { RedirectScreen } from "./successPage/RedirectScreen";
import type { SuccessPageConfig } from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { VocationIndustry } from "@portal/app/_common/types/vocationIndustry";

interface SuccessPageProps {
  programName: string;
  providerName: string;
  providerLogoUrl?: string;
  /**
   * Provider-level "what to expect" copy (Provider.successPageMessage).
   * Highest-precedence body override: when set, it replaces both the
   * funnel-level message override and the default body for every screen
   * variant.
   */
  providerMessage?: string | null;
  industry?: VocationIndustry;
  hasFinancialAid?: boolean;
  firstName?: string;
  config?: SuccessPageConfig;
  redirectUrl?: string;
  redirectDelaySeconds?: number;
}

const DEFAULT_TITLE = "You'll get a call soon";

const buildDefaultBody = (
  providerName: string,
  programName: string,
  screen: "A" | "B" | "C",
): string => {
  const base = `${providerName} will call within 1 business day about your ${programName} program`;
  switch (screen) {
    case "A":
      return `${base}. Start your app now so the call goes faster.`;
    case "B":
      return `${base}.`;
    case "C":
      return `${base} to walk you through start dates, cost, and payment options. No prep needed.`;
  }
};

const interpolateMessage = (
  message: string,
  programName: string,
  providerName: string,
  firstName?: string,
): string =>
  message
    .replace(/\{\{programName\}\}/g, programName)
    .replace(/\{\{providerName\}\}/g, providerName)
    .replace(/\{\{firstName\}\}/g, firstName?.trim() || "");

export const SuccessPage = ({
  programName,
  providerName,
  providerLogoUrl,
  providerMessage,
  industry,
  hasFinancialAid = false,
  firstName,
  config,
  redirectUrl,
  redirectDelaySeconds,
}: SuccessPageProps) => {
  const screen: "A" | "B" | "C" = redirectUrl
    ? "A"
    : hasFinancialAid
      ? "B"
      : "C";

  const title = resolveSuccessTitle(config?.title);

  const overrideMessage = config?.message?.trim()
    ? interpolateMessage(config.message, programName, providerName, firstName)
    : null;
  const providerText = providerMessage?.trim()
    ? interpolateMessage(providerMessage, programName, providerName, firstName)
    : null;
  const body =
    providerText ?? overrideMessage ?? buildDefaultBody(providerName, programName, screen);
  const isBodyMarkdown = !!providerText || !!overrideMessage;

  // Image and video are mutually exclusive in the CMS; if both arrive, prefer the video.
  const hasVideo = config?.videoUrl ? parseVideoEmbed(config.videoUrl) !== null : false;
  const imageUrl =
    !hasVideo && config?.imageUrl?.startsWith("https://") ? config.imageUrl : undefined;

  // Override CTA: applies to A's primary button copy and to B's primary button entirely.
  // For C (no default primary CTA), an override creates a primary button.
  const ctaUrlOverride = config?.ctaUrl?.startsWith("/") ? config.ctaUrl : undefined;
  const ctaTextOverride = config?.ctaText?.trim() || undefined;

  return (
    <Box
      data-testid="success-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        p: 4,
        textAlign: "center",
        maxWidth: 600,
        mx: "auto",
        mt: 4,
      }}
    >
      <BrandHeader providerName={providerName} providerLogoUrl={providerLogoUrl} />

      {hasVideo && (
        <Box sx={{ mb: 1, width: "100%", display: "flex", justifyContent: "center" }}>
          <VideoEmbed videoUrl={config?.videoUrl} testId="success-page-video" />
        </Box>
      )}

      {imageUrl && (
        <Box sx={{ mb: 1 }}>
          <img
            src={imageUrl}
            alt=""
            style={{ maxWidth: "100%", height: "auto", maxHeight: 200, objectFit: "contain" }}
          />
        </Box>
      )}

      {screen === "A" && (
        <RedirectScreen
          title={title}
          body={body}
          isBodyMarkdown={isBodyMarkdown}
          redirectUrl={redirectUrl!}
          redirectDelaySeconds={redirectDelaySeconds}
          ctaTextOverride={ctaTextOverride}
        />
      )}
      {screen === "B" && (
        <FinancialAidScreen
          title={title}
          body={body}
          isBodyMarkdown={isBodyMarkdown}
          industry={industry}
          primaryCtaOverride={
            ctaTextOverride && ctaUrlOverride
              ? { text: ctaTextOverride, href: ctaUrlOverride }
              : undefined
          }
        />
      )}
      {screen === "C" && (
        <DefaultScreen
          title={title}
          body={body}
          isBodyMarkdown={isBodyMarkdown}
          industry={industry}
          primaryCta={
            ctaTextOverride && ctaUrlOverride
              ? { text: ctaTextOverride, href: ctaUrlOverride }
              : undefined
          }
        />
      )}
    </Box>
  );
};

const resolveSuccessTitle = (configuredTitle?: string) => {
  if (!configuredTitle) return DEFAULT_TITLE;

  return configuredTitle === "You're in the queue." ||
    configuredTitle === "You’re in the queue." ||
    configuredTitle === "You're in the queue" ||
    configuredTitle === "You’re in the queue"
    ? DEFAULT_TITLE
    : configuredTitle;
};
